import React from 'react';
import { Row, Col, Select, Input, Button, Switch, Form, Space, Avatar } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { PLAN_OWNERS } from '../../graphql/queries/planOwners';
import { IPlanOwner } from '../../types/planOwner';
import { SIZES_RANGE } from '../../consts/sizes';
import { IBaseFilter } from '../../types/plans';

const { Option } = Select;

interface AdminPlanFiltersProps {
  filters: IBaseFilter;
  setFilters: (filters: IBaseFilter) => void;
  roles?: string[];
  setUnlocked?: (unlocked: boolean) => void;
}

const AdminPlanFilters: React.FC<AdminPlanFiltersProps> = ({
  filters,
  setFilters,
  roles = [],
  setUnlocked = null,
}) => {
  const isAdmin = roles?.includes('ADMIN');
  const { data, refetch } = useQuery<{ planOwners: IPlanOwner[] }>(PLAN_OWNERS);
  const planOwners = (data && data.planOwners) || [];

  const handleChange = (value: string | number | object | boolean, field: string) => {
    if (setUnlocked) setUnlocked(false);
    if (typeof value === 'object' && value !== null) {
      if (!filters[field]) filters[field] = {};
      const fieldValue = typeof filters[field] === 'object' ? (filters[field] as object) : {};
      setFilters({
        ...filters,
        [field]: { ...fieldValue, ...(value as object) },
      });
      return;
    }
    setFilters({ ...filters, [field]: value });
  };

  const handleChangeSize = (
    e: React.ChangeEvent<HTMLInputElement>,
    sizeField: string,
    valueField: string
  ) => {
    if (setUnlocked) setUnlocked(false);
    const newState = { ...filters };
    if (!newState.meta) newState.meta = {};
    if (!newState.meta[sizeField]) {
      newState.meta = {
        ...newState.meta,
        [sizeField]: {
          [valueField]: +e.target.value,
        },
      };
      setFilters(newState);
      return;
    }

    newState.meta[sizeField] = {
      ...(newState.meta[sizeField] as object),
      [valueField]: +e.target.value,
    };
    setFilters(newState);
  };

  const onDropFilter = () => {
    setFilters({ planOwnerId: '' });
    refetch();
  };

  const getValueArea = () => {
    if (!filters.meta || !filters.meta.outerArea) return null;
    const key = `${filters.meta.outerArea.gte}${filters.meta.outerArea.lte ? ' - ' + filters.meta.outerArea.lte : '+'}`;
    return key;
  };

  return (
    <Form layout="horizontal" style={{ width: '100%' }}>
      <Row gutter={[16, 0]}>
        {isAdmin && (
          <Col span={24} lg={5}>
            <Form.Item>
              <Select
                placeholder="Any plan owner"
                value={filters.planOwnerId}
                style={{ width: '100%' }}
                onChange={value => handleChange(value, 'planOwnerId')}>
                <Option key="any" value="">
                  Any plan owner
                </Option>
                {planOwners.map((planOwner: IPlanOwner) => (
                  <Option key={planOwner.id} value={planOwner.id}>
                    <Avatar style={{ marginRight: 5 }} src={planOwner.logo} />
                    {planOwner.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
        {isAdmin && (
          <Col span={24} lg={3}>
            <Form.Item>
              <Select
                placeholder="Type"
                value={filters.meta?.type}
                style={{ width: '100%' }}
                onChange={value => handleChange({ type: value }, 'meta')}>
                <Option key="all" value="">
                  All
                </Option>
                <Option key="house" value="house">
                  House
                </Option>
                <Option key="shed" value="shed">
                  Shed
                </Option>
              </Select>
            </Form.Item>
          </Col>
        )}
        <Col span={24} lg={isAdmin ? 4 : 8}>
          <Form.Item>
            <Select
              placeholder="Sq Ft"
              value={getValueArea()}
              style={{ width: '100%' }}
              onChange={value => handleChange({ outerArea: SIZES_RANGE[value] }, 'meta')}>
              <Option key="all" value="">
                All
              </Option>
              {Object.entries(SIZES_RANGE).map(([key]) => (
                <Option key={key} value={key}>
                  {key}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={24} lg={isAdmin ? 6 : 8}>
          <Form.Item>
            <Select
              placeholder="Bedrooms"
              value={filters.meta?.bedroomCount}
              style={{ width: '100%' }}
              onChange={value => handleChange({ bedroomCount: value }, 'meta')}>
              <Option key="0" value={0}>
                Studio
              </Option>
              <Option key="1" value={1}>
                1
              </Option>
              <Option key="2" value={2}>
                2
              </Option>
              <Option key="3" value={3}>
                3
              </Option>
              <Option key="4" value={4}>
                4
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={24} lg={isAdmin ? 6 : 8}>
          <Form.Item>
            <Select
              placeholder="Bathrooms"
              value={filters.meta?.bathroomCount}
              style={{ width: '100%' }}
              onChange={value => handleChange({ bathroomCount: value }, 'meta')}>
              <Option key="bathroomCount-0" value={0}>
                0
              </Option>
              <Option key="bathroomCount-1" value={1}>
                1
              </Option>
              <Option key="bathroomCount-2" value={2}>
                2
              </Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]} align="bottom" justify="space-between">
        <Col span={24} lg={16}>
          <Row gutter={[16, 0]}>
            <Col span={24} lg={12}>
              <Form.Item label="Width, ft">
                <Space.Compact>
                  <Input
                    addonBefore="from"
                    type="number"
                    min={0}
                    value={filters.meta?.width?.gte}
                    placeholder="0"
                    onChange={e => handleChangeSize(e, 'width', 'gte')}
                  />
                  <Input
                    addonBefore="to"
                    type="number"
                    min={0}
                    value={filters.meta?.width?.lte}
                    placeholder="100"
                    onChange={e => handleChangeSize(e, 'width', 'lte')}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col span={24} lg={12}>
              <Form.Item label="Length, ft">
                <Space.Compact>
                  <Input
                    addonBefore="from"
                    type="number"
                    min={0}
                    value={filters.meta?.length?.gte}
                    placeholder="0"
                    onChange={e => handleChangeSize(e, 'length', 'gte')}
                  />
                  <Input
                    addonBefore="to"
                    type="number"
                    min={0}
                    value={filters.meta?.length?.lte}
                    placeholder="100"
                    onChange={e => handleChangeSize(e, 'length', 'lte')}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col span={24} lg={8}>
          <Row gutter={[16, 0]} justify="space-between">
            {isAdmin && (
              <Col span={24} lg={12}>
                <Form.Item label="Hidden">
                  <Switch
                    checked={filters.isHidden}
                    onChange={() => handleChange(!filters.isHidden, 'isHidden')}
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24} lg={12}>
              <Form.Item>
                <Button style={{ width: '100%' }} type="dashed" onClick={onDropFilter}>
                  <RedoOutlined />
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Col>
        {isAdmin && (
          <Col span={24} lg={4}>
            <Form.Item label="Pre Approved">
              <Switch
                checked={filters.preApproved}
                onChange={() => handleChange(!filters.preApproved, 'preApproved')}
              />
            </Form.Item>
          </Col>
        )}
        {isAdmin && (
          <Col span={24} lg={4}>
            <Form.Item label="planId">
              <Space.Compact>
                <Input
                  type="text"
                  value={filters.planId}
                  onChange={e => handleChange(e.target.value, 'planId')}
                />
              </Space.Compact>
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default AdminPlanFilters;
